/**
 * Desktop menu dropdown functionality
 * 
 * Handles dropdown menu toggle behavior for desktop navigation
 */
export function initDesktopMenu() {
	const dropdowns = document.querySelectorAll('#site-navigation .dropdown');
	
	if (!dropdowns.length) {
		return;
	}
	
	/**
	 * Adjust dropdown positioning based on viewport edges
	 * @param {HTMLElement} dropdown - The dropdown container element
	 * @param {HTMLElement} menu - The dropdown menu element
	 */
	function adjustDropdownPosition(dropdown, menu) {
		// Store original visibility state
		const wasVisible = menu.classList.contains('opacity-100');
		
		// Temporarily make menu visible but hidden to measure it
		// Remove opacity and visibility classes, but keep it visually hidden
		menu.classList.remove('opacity-0', 'invisible');
		menu.style.position = 'absolute';
		menu.style.visibility = 'hidden';
		menu.style.opacity = '0';
		menu.style.display = 'block';
		
		// Ensure menu has left-0 for initial measurement
		menu.classList.remove('right-0');
		menu.classList.add('left-0');
		
		// Force a reflow to ensure measurements are accurate
		void menu.offsetWidth;
		
		// Get bounding rectangles
		const dropdownRect = dropdown.getBoundingClientRect();
		const menuRect = menu.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		const viewportPadding = 16; // 1rem padding from viewport edges
		
		// Calculate positions
		const menuWidth = menuRect.width || menu.offsetWidth;
		const leftPosition = dropdownRect.left;
		const rightPosition = dropdownRect.right;
		
		// Check if menu would overflow on the right when aligned to left
		const wouldOverflowRight = leftPosition + menuWidth > viewportWidth - viewportPadding;
		
		// Check if menu would overflow on the left if aligned to right
		const wouldOverflowLeft = rightPosition - menuWidth < viewportPadding;
		
		// Remove existing positioning classes
		menu.classList.remove('left-0', 'right-0');
		
		// Determine which alignment to use
		let useRightAlignment = false;
		if (wouldOverflowRight && !wouldOverflowLeft) {
			useRightAlignment = true;
		}
		
		// Apply appropriate positioning
		if (useRightAlignment) {
			// Align to right edge of parent
			menu.classList.add('right-0');
		} else {
			// Default: align to left edge of parent
			menu.classList.add('left-0');
		}
		
		// Force a reflow after alignment change
		void menu.offsetWidth;
		
		// Set max-width to prevent overflow regardless of alignment
		let maxWidth;
		
		if (useRightAlignment) {
			// When right-aligned, menu's right edge aligns with parent's right edge
			// Max width is from parent's right edge to viewport left edge (with padding)
			maxWidth = rightPosition - viewportPadding;
		} else {
			// When left-aligned, menu's left edge aligns with parent's left edge
			// Max width is from parent's left edge to viewport right edge (with padding)
			maxWidth = viewportWidth - leftPosition - viewportPadding;
		}
		
		// Ensure max-width is positive and reasonable
		if (maxWidth > 0 && maxWidth < menuWidth) {
			menu.style.maxWidth = `${maxWidth}px`;
		} else if (maxWidth <= 0) {
			// If calculated max-width is invalid, use a safe fallback
			menu.style.maxWidth = `${Math.max(200, viewportWidth - viewportPadding * 2)}px`;
		} else {
			menu.style.maxWidth = '';
		}
		
		// Restore original visibility state
		menu.style.visibility = '';
		menu.style.opacity = '';
		menu.style.display = '';
		if (!wasVisible) {
			menu.classList.add('opacity-0', 'invisible');
		}
	}
	
	/**
	 * Open a dropdown menu
	 * @param {HTMLElement} dropdown - The dropdown container element
	 * @param {HTMLElement} toggle - The toggle button element
	 * @param {HTMLElement} menu - The dropdown menu element
	 * @param {HTMLElement|null} chevron - The chevron icon element
	 */
	function openDropdown(dropdown, toggle, menu, chevron) {
		// Adjust position before opening
		adjustDropdownPosition(dropdown, menu);
		
		dropdown.classList.add('is-open');
		if (toggle) {
			toggle.setAttribute('aria-expanded', 'true');
		}
		menu.classList.remove('opacity-0', 'invisible', 'translate-y-[-10px]');
		menu.classList.add('opacity-100', 'visible', 'translate-y-0');
		if (chevron) {
			chevron.classList.add('rotate-180');
		}
	}
	
	/**
	 * Close a dropdown menu
	 * @param {HTMLElement} dropdown - The dropdown container element
	 * @param {HTMLElement} toggle - The toggle button element
	 * @param {HTMLElement} menu - The dropdown menu element
	 * @param {HTMLElement|null} chevron - The chevron icon element
	 */
	function closeDropdown(dropdown, toggle, menu, chevron) {
		dropdown.classList.remove('is-open');
		if (toggle) {
			toggle.setAttribute('aria-expanded', 'false');
		}
		menu.classList.remove('opacity-100', 'visible', 'translate-y-0');
		menu.classList.add('opacity-0', 'invisible', 'translate-y-[-10px]');
		if (chevron) {
			chevron.classList.remove('rotate-180');
		}
	}
	
	/**
	 * Close all dropdowns except the specified one
	 * @param {HTMLElement} excludeDropdown - The dropdown to exclude from closing
	 */
	function closeAllDropdowns(excludeDropdown = null) {
		dropdowns.forEach((otherDropdown) => {
			if (otherDropdown !== excludeDropdown) {
				const otherToggle = otherDropdown.querySelector('.dropdown-toggle');
				const otherMenu = otherDropdown.querySelector('.dropdown-menu');
				const otherChevron = otherToggle ? otherToggle.querySelector('svg') : null;
				closeDropdown(otherDropdown, otherToggle, otherMenu, otherChevron);
			}
		});
	}
	
	dropdowns.forEach((dropdown) => {
		const toggle = dropdown.querySelector('.dropdown-toggle');
		const menu = dropdown.querySelector('.dropdown-menu');
		const chevron = toggle ? toggle.querySelector('svg') : null;
		
		if (!toggle || !menu) {
			return;
		}
		
		// Open dropdown on hover (mouseenter on <li>)
		dropdown.addEventListener('mouseenter', () => {
			closeAllDropdowns(dropdown);
			openDropdown(dropdown, toggle, menu, chevron);
		});
		
		// Close dropdown when mouse leaves the <li> element
		dropdown.addEventListener('mouseleave', () => {
			closeDropdown(dropdown, toggle, menu, chevron);
		});
		
		// Toggle dropdown on click
		toggle.addEventListener('click', (e) => {
			// Check if this is a button parent item (has a real URL, not '#')
			// Button parent items should be clickable to navigate, not toggle dropdown
			const href = toggle.getAttribute('href');
			const isButton = href && href !== '#' && href !== '';
			
			if (isButton) {
				// This is a button parent item - allow navigation, don't toggle dropdown
				// Don't prevent default, let the link work normally
				return;
			}
			
			// For non-button parent items, prevent default and toggle dropdown
			e.preventDefault();
			
			const isOpen = dropdown.classList.contains('is-open');
			
			// Close all other dropdowns
			closeAllDropdowns(dropdown);
			
			// Toggle current dropdown
			if (isOpen) {
				closeDropdown(dropdown, toggle, menu, chevron);
			} else {
				openDropdown(dropdown, toggle, menu, chevron);
			}
		});
		
		// Close dropdown when clicking outside
		document.addEventListener('click', (e) => {
			if (!dropdown.contains(e.target)) {
				closeDropdown(dropdown, toggle, menu, chevron);
			}
		});
		
		// Close dropdown on escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && dropdown.classList.contains('is-open')) {
				closeDropdown(dropdown, toggle, menu, chevron);
			}
		});
		
		// Recalculate position on window resize
		let resizeTimeout;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				if (dropdown.classList.contains('is-open')) {
					adjustDropdownPosition(dropdown, menu);
				}
			}, 100);
		});
	});
}

